import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import { invalidateDashboardOverview } from '@/hooks/queries/dashboard-overview-keys';
import {
  CONTACT_SUMMARY_QUERY_PREFIX,
  contactSummaryQueryPrefix,
} from '@/hooks/queries/use-contact-summary-query';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

/** Base path for every dashboard collab endpoint (FOU-834 tracking surface). */
export const COLLABS_PATH = '/dashboard/collabs';

/**
 * Query-key factory for tracked collabs. Keyed on brand so each brand caches
 * independently. Search, group, sort, type and recipient filters all resolve
 * server-side, so every filtered page lives under its own list key; counts and
 * per-collab detail/events stay addressable on their own so mutations and the
 * realtime bridge can invalidate them precisely.
 */
export const collabKeys = {
  /**
   * The list key carries the whole server-filter set (page, search, group,
   * sort, type, recipients) as an object suffix. The bare no-params key is the
   * prefix, so `invalidateQueries(list(brandId))` matches every filtered page,
   * the banner variant and the contact-card variant alike.
   */
  list: (brandId: string, params?: Record<string, unknown>) =>
    params && Object.keys(params).length > 0
      ? (['collabs', brandId, 'list', params] as const)
      : (['collabs', brandId, 'list'] as const),
  /** The recipient options feeding the creator filter (server-searchable). */
  recipients: (brandId: string, search: string) =>
    ['collabs', brandId, 'recipients', search] as const,
  counts: (brandId: string) => ['collabs', brandId, 'counts'] as const,
  /** The UGC page's "to handle" banner, whose length badges the menu entry. */
  ugcAttention: (brandId: string) => ['collabs', brandId, 'ugc-attention'] as const,
  /** The Contenus view of the UGC page, one key per filter set under one prefix. */
  ugcContents: (brandId: string, params?: Record<string, unknown>) =>
    params
      ? (['collabs', brandId, 'ugc-contents', params] as const)
      : (['collabs', brandId, 'ugc-contents'] as const),
  deliverables: (brandId: string, params?: Record<string, unknown>) =>
    params && Object.keys(params).length > 0
      ? (['collabs', brandId, 'deliverables', params] as const)
      : (['collabs', brandId, 'deliverables'] as const),
  detail: (brandId: string, collabId: string) => ['collabs', brandId, 'detail', collabId] as const,
  events: (brandId: string, collabId: string) => ['collabs', brandId, 'events', collabId] as const,
};

/**
 * Invalidate every query a collab mutation can stale: the list, the counts,
 * this collab's detail + events, and the contact summaries. The summary feeds
 * the contact rail's "{n} en cours" label and holds a 5 min staleTime, so
 * skipping it would leave the rail count wrong after a close / withdraw /
 * send. Prefix invalidation: the summary key is identity-scoped and the
 * mutation only knows the collab id. One place so the three action hooks
 * (remind / withdraw / close) can never drift on the invalidation set.
 */
export const invalidateCollabQueries = (
  queryClient: QueryClient,
  brandId: string,
  collabId: string
): void => {
  invalidateCollabListQueries(queryClient, brandId);
  queryClient.invalidateQueries({ queryKey: collabKeys.deliverables(brandId) });
  queryClient.invalidateQueries({ queryKey: collabKeys.detail(brandId, collabId) });
  queryClient.invalidateQueries({ queryKey: collabKeys.events(brandId, collabId) });
};

/**
 * Everything a *new* collab staled: the list, the counts, and the contact
 * summaries feeding the rail's "{n} en cours" label. No detail and no events —
 * a collab that did not exist a second ago has nothing cached under its id.
 *
 * Split out of {@link invalidateCollabQueries} for the send, which creates
 * collabs it has no single id for: a bulk send makes one per recipient.
 */
export const invalidateCollabListQueries = (queryClient: QueryClient, brandId: string): void => {
  queryClient.invalidateQueries({ queryKey: collabKeys.list(brandId) });
  queryClient.invalidateQueries({ queryKey: collabKeys.counts(brandId) });
  queryClient.invalidateQueries({ queryKey: collabKeys.ugcAttention(brandId) });
  // The dashboard to-do card counts the UGC decisions the badge counts.
  invalidateDashboardOverview(queryClient);
  queryClient.invalidateQueries({ queryKey: collabKeys.ugcContents(brandId) });
  queryClient.invalidateQueries({ queryKey: contactSummaryQueryPrefix(brandId) });
};

/**
 * User-level invalidation for the realtime bridge: a collab wakeup event
 * carries no brand id, so every brand's collab queries AND every contact
 * summary refetch (the summary feeds the contact rail's "{n} en cours" label).
 * Mirrors invalidateCollabQueries' scope, one level up.
 */
export const invalidateCollabRealtimeQueries = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries({ queryKey: ['collabs'] });
  invalidateDashboardOverview(queryClient);
  queryClient.invalidateQueries({ queryKey: [...CONTACT_SUMMARY_QUERY_PREFIX] });
};

/**
 * Shared mutation plumbing: resolves the current brand and returns an
 * invalidator for one collab. Collab action hooks call this instead of copying
 * the brandId resolution + four-key invalidation boilerplate.
 */
export const useInvalidateCollabQueries = (): ((collabId: string) => void) => {
  const queryClient = useQueryClient();
  const { data: brand } = useCurrentBrandQuery();
  const brandId = brand?.id ?? '';
  return (collabId: string) => invalidateCollabQueries(queryClient, brandId, collabId);
};

/**
 * Same plumbing for the wizard send: the collabs it just created must show up
 * on "Mes collabs" without a manual reload. Doubting the most important gesture
 * of the product is the one thing an empty list after a success cannot afford.
 */
export const useInvalidateCollabList = (): (() => void) => {
  const queryClient = useQueryClient();
  const { data: brand } = useCurrentBrandQuery();
  const brandId = brand?.id ?? '';
  return () => invalidateCollabListQueries(queryClient, brandId);
};
